import React, { useEffect, useMemo, useState } from "react"
import {
  changeSubscriptionPlan,
  getClinicSubscriptionStatus,
  getSubscriptionPlans,
  previewSubscriptionChange,
} from "../api/subscriptionsApi"

const money = new Intl.NumberFormat("es-MX", {
  style: "currency",
  currency: "MXN",
  maximumFractionDigits: 2,
})

const dateTime = new Intl.DateTimeFormat("es-MX", {
  dateStyle: "medium",
  timeStyle: "short",
})

const dateOnly = new Intl.DateTimeFormat("es-MX", {
  dateStyle: "medium",
})

function formatMoney(value) {
  return money.format(Number(value || 0))
}

function formatDateTime(value) {
  if (!value) return "—"
  return dateTime.format(new Date(value))
}

function formatDate(value) {
  if (!value) return "—"
  return dateOnly.format(new Date(value))
}

function getActionLabel(action) {
  switch (action) {
    case "ACTIVATION":
      return "Activación"
    case "REACTIVATION":
      return "Reactivación"
    case "UPGRADE_IMMEDIATE":
      return "Upgrade inmediato"
    case "DOWNGRADE_SCHEDULED":
      return "Downgrade programado"
    case "TRIAL_CHANGE_IMMEDIATE":
      return "Cambio inmediato durante trial"
    case "LATERAL_IMMEDIATE":
      return "Cambio lateral inmediato"
    case "SCHEDULE_CYCLE_CHANGE":
      return "Cambio de ciclo programado"
    case "CLEAR_SCHEDULED_CHANGE":
      return "Cancelar cambio programado"
    case "NO_CHANGE":
      return "Sin cambios"
    case "BLOCKED_PAST_DUE":
      return "Bloqueado por adeudo"
    default:
      return action || "—"
  }
}

function getModeLabel(mode) {
  switch (mode) {
    case "IMMEDIATE":
      return "Inmediato"
    case "SCHEDULED":
      return "Programado"
    case "BLOCKED":
      return "Bloqueado"
    case "NONE":
      return "Sin acción"
    default:
      return mode || "—"
  }
}

function getBillingCycleLabel(cycle) {
  switch (cycle) {
    case "MONTHLY":
      return "Mensual"
    case "YEARLY":
      return "Anual"
    default:
      return "—"
  }
}

function getStatusLabel(status) {
  switch (status) {
    case "ACTIVE":
      return "Activa"
    case "PAST_DUE":
      return "Con adeudo"
    case "TRIALING":
      return "En trial"
    case "CANCELLED":
      return "Cancelada"
    case "EXPIRED":
      return "Expirada"
    default:
      return status || "—"
  }
}

function getPlanFeatureItems(plan) {
  return [
    {
      label: "Clínicas",
      value:
        plan?.maxClinics == null
          ? "Ilimitadas"
          : String(plan.maxClinics),
    },
    {
      label: "Doctores",
      value:
        plan?.maxDoctors == null
          ? "Ilimitados"
          : String(plan.maxDoctors),
    },
    {
      label: "Usuarios",
      value:
        plan?.maxUsers == null
          ? "Ilimitados"
          : String(plan.maxUsers),
    },
    {
      label: "Sillones",
      value:
        plan?.maxChairs == null
          ? "Ilimitados"
          : String(plan.maxChairs),
    },
    {
      label: "Dashboard ejecutivo",
      value: plan?.allowExecutiveDashboard ? "Sí" : "No",
    },
    {
      label: "Auditoría",
      value: plan?.allowAudit ? "Sí" : "No",
    },
    {
      label: "Reportes avanzados",
      value: plan?.allowAdvancedReports ? "Sí" : "No",
    },
    {
      label: "IA",
      value: plan?.allowAI ? "Sí" : "No",
    },
    {
      label: "Multi-clínica",
      value: plan?.allowMultiClinic ? "Sí" : "No",
    },
  ]
}

function Badge({ children, tone = "neutral" }) {
  const palette = {
    neutral: {
      background: "#EEF2FF",
      color: "#3730A3",
      border: "#C7D2FE",
    },
    success: {
      background: "#ECFDF3",
      color: "#027A48",
      border: "#A6F4C5",
    },
    warning: {
      background: "#FFFAEB",
      color: "#B54708",
      border: "#FEDF89",
    },
    danger: {
      background: "#FEF3F2",
      color: "#B42318",
      border: "#FECDCA",
    },
    info: {
      background: "#EFF8FF",
      color: "#175CD3",
      border: "#B2DDFF",
    },
  }

  const style = palette[tone] || palette.neutral

  return (
    <span
      style={{
        display: "inline-flex",
        alignItems: "center",
        padding: "6px 10px",
        borderRadius: 999,
        fontSize: 12,
        fontWeight: 700,
        background: style.background,
        color: style.color,
        border: `1px solid ${style.border}`,
      }}
    >
      {children}
    </span>
  )
}

function SectionCard({ title, subtitle, right, children }) {
  return (
    <div
      style={{
        background: "#FFFFFF",
        border: "1px solid #E5E7EB",
        borderRadius: 18,
        padding: 20,
        boxShadow: "0 8px 24px rgba(15, 23, 42, 0.05)",
      }}
    >
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          gap: 12,
          flexWrap: "wrap",
          marginBottom: 16,
        }}
      >
        <div>
          <div
            style={{
              fontSize: 18,
              fontWeight: 800,
              color: "#0F172A",
            }}
          >
            {title}
          </div>
          {subtitle ? (
            <div
              style={{
                marginTop: 4,
                fontSize: 14,
                color: "#64748B",
              }}
            >
              {subtitle}
            </div>
          ) : null}
        </div>

        {right ? <div>{right}</div> : null}
      </div>

      {children}
    </div>
  )
}

function InfoRow({ label, value, valueTone = "#0F172A" }) {
  return (
    <div
      style={{
        display: "flex",
        justifyContent: "space-between",
        gap: 12,
        padding: "10px 0",
        borderBottom: "1px solid #F1F5F9",
      }}
    >
      <div style={{ color: "#64748B", fontSize: 14 }}>{label}</div>
      <div
        style={{
          color: valueTone,
          fontSize: 14,
          fontWeight: 700,
          textAlign: "right",
        }}
      >
        {value}
      </div>
    </div>
  )
}

function PlanCard({
  plan,
  isCurrent,
  isSelected,
  onSelect,
  selectedBillingCycle,
}) {
  const monthly = formatMoney(plan.priceMonthly)
  const yearly =
    plan.priceYearly != null ? formatMoney(plan.priceYearly) : "—"

  return (
    <button
      type="button"
      onClick={() => onSelect(plan.code)}
      style={{
        textAlign: "left",
        width: "100%",
        background: isSelected ? "#EEF2FF" : "#FFFFFF",
        border: isSelected
          ? "2px solid #4F46E5"
          : "1px solid #E5E7EB",
        borderRadius: 18,
        padding: 18,
        cursor: "pointer",
        transition: "all 0.15s ease",
        boxShadow: isSelected
          ? "0 10px 25px rgba(79, 70, 229, 0.10)"
          : "0 6px 18px rgba(15, 23, 42, 0.04)",
      }}
    >
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          gap: 10,
          alignItems: "flex-start",
          marginBottom: 12,
          flexWrap: "wrap",
        }}
      >
        <div>
          <div
            style={{
              fontSize: 18,
              fontWeight: 800,
              color: "#0F172A",
            }}
          >
            {plan.name}
          </div>
          <div
            style={{
              marginTop: 4,
              fontSize: 13,
              color: "#64748B",
            }}
          >
            {plan.code}
          </div>
        </div>

        <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
          {isCurrent ? <Badge tone="success">Plan actual</Badge> : null}
          {isSelected ? <Badge tone="info">Seleccionado</Badge> : null}
        </div>
      </div>

      {plan.description ? (
        <div
          style={{
            fontSize: 14,
            color: "#475569",
            marginBottom: 12,
            lineHeight: 1.5,
          }}
        >
          {plan.description}
        </div>
      ) : null}

      <div
        style={{
          display: "grid",
          gridTemplateColumns: "repeat(auto-fit, minmax(150px, 1fr))",
          gap: 10,
          marginBottom: 14,
        }}
      >
        <div
          style={{
            background: "#F8FAFC",
            borderRadius: 14,
            padding: 12,
            border: "1px solid #E2E8F0",
          }}
        >
          <div style={{ fontSize: 12, color: "#64748B", marginBottom: 4 }}>
            Precio mensual
          </div>
          <div style={{ fontSize: 18, fontWeight: 800, color: "#0F172A" }}>
            {monthly}
          </div>
        </div>

        <div
          style={{
            background: "#F8FAFC",
            borderRadius: 14,
            padding: 12,
            border: "1px solid #E2E8F0",
          }}
        >
          <div style={{ fontSize: 12, color: "#64748B", marginBottom: 4 }}>
            Precio anual
          </div>
          <div style={{ fontSize: 18, fontWeight: 800, color: "#0F172A" }}>
            {yearly}
          </div>
        </div>
      </div>

      <div
        style={{
          padding: 12,
          background: selectedBillingCycle === "YEARLY" ? "#FFFBEB" : "#F8FAFC",
          borderRadius: 14,
          border: "1px solid #E5E7EB",
          marginBottom: 12,
        }}
      >
        <div style={{ fontSize: 12, color: "#64748B", marginBottom: 4 }}>
          Precio estimado en el ciclo elegido
        </div>
        <div style={{ fontSize: 20, fontWeight: 800, color: "#111827" }}>
          {selectedBillingCycle === "YEARLY"
            ? yearly
            : monthly}
        </div>
      </div>

      <div
        style={{
          display: "grid",
          gridTemplateColumns: "repeat(auto-fit, minmax(140px, 1fr))",
          gap: 8,
        }}
      >
        {getPlanFeatureItems(plan).map((item) => (
          <div
            key={`${plan.code}-${item.label}`}
            style={{
              background: "#FFFFFF",
              border: "1px solid #E5E7EB",
              borderRadius: 12,
              padding: 10,
            }}
          >
            <div style={{ fontSize: 12, color: "#64748B" }}>{item.label}</div>
            <div
              style={{
                marginTop: 4,
                fontSize: 14,
                fontWeight: 700,
                color: "#0F172A",
              }}
            >
              {item.value}
            </div>
          </div>
        ))}
      </div>
    </button>
  )
}

export default function SubscriptionAdminPage() {
  const [clinicId] = useState(
    window.localStorage.getItem("odontoflow_clinic_id") || "",
    )

  const [loading, setLoading] = useState(true)
  const [loadingPreview, setLoadingPreview] = useState(false)
  const [saving, setSaving] = useState(false)

  const [plans, setPlans] = useState([])
  const [statusData, setStatusData] = useState(null)
  const [previewData, setPreviewData] = useState(null)

  const [selectedPlanCode, setSelectedPlanCode] = useState("")
  const [selectedBillingCycle, setSelectedBillingCycle] = useState("MONTHLY")

  const [error, setError] = useState("")
  const [success, setSuccess] = useState("")

  const subscription = statusData?.subscription || null
  const currentPlan = statusData?.plan || null
  const trial = statusData?.trial || null
  const access = statusData?.access || null

  const selectedPlan = useMemo(() => {
    return plans.find((item) => item.code === selectedPlanCode) || null
  }, [plans, selectedPlanCode])

  const scheduledChange = useMemo(() => {
    if (!subscription) return null

    if (
      subscription.nextPlan ||
      subscription.scheduledChangeAt ||
      subscription.cancelAtPeriodEnd ||
      subscription.nextBillingCycle
    ) {
      return {
        nextPlan: subscription.nextPlan || null,
        scheduledChangeAt: subscription.scheduledChangeAt || null,
        scheduledChangeType: subscription.scheduledChangeType || null,
        nextBillingCycle: subscription.nextBillingCycle || null,
        cancelAtPeriodEnd: Boolean(subscription.cancelAtPeriodEnd),
      }
    }

    return null
  }, [subscription])

  async function loadData(currentClinicId) {
    if (!currentClinicId) {
      setLoading(false)
      setStatusData(null)
      setPlans([])
      return
    }

    setLoading(true)
    setError("")
    setSuccess("")

    try {
      const [plansResult, statusResult] = await Promise.all([
        getSubscriptionPlans(),
        getClinicSubscriptionStatus(currentClinicId),
      ])

      setPlans(plansResult || [])
      setStatusData(statusResult || null)

      const currentPlanCode = statusResult?.plan?.code || ""
      const currentBillingCycle =
        statusResult?.subscription?.billingCycle || "MONTHLY"

      setSelectedPlanCode((prev) => prev || currentPlanCode || "")
      setSelectedBillingCycle((prev) => prev || currentBillingCycle || "MONTHLY")
    } catch (err) {
      setError(err.message || "No se pudo cargar la información de suscripción")
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    loadData(clinicId)
  }, [clinicId])

  async function handlePreview() {
    if (!clinicId) {
      setError("No hay clínica seleccionada")
      return
    }

    if (!selectedPlanCode) {
      setError("Selecciona un plan")
      return
    }

    setLoadingPreview(true)
    setError("")
    setSuccess("")

    try {
      const result = await previewSubscriptionChange({
        clinicId,
        planCode: selectedPlanCode,
        billingCycle: selectedBillingCycle,
      })

      setPreviewData(result)
    } catch (err) {
      setError(err.message || "No se pudo generar la vista previa")
    } finally {
      setLoadingPreview(false)
    }
  }

  async function handleApply() {
    if (!previewData) {
      setError("Primero genera la vista previa")
      return
    }

    if (!previewData.allowed) {
      setError(previewData.reason || "Este cambio no puede aplicarse")
      return
    }

    const confirmMessage = [
      `Acción: ${getActionLabel(previewData.action)}`,
      `Modo: ${getModeLabel(previewData.mode)}`,
      previewData.reason ? `Motivo: ${previewData.reason}` : "",
      previewData.billing?.immediateCharge > 0
        ? `Cobro inmediato estimado: ${formatMoney(
            previewData.billing.immediateCharge,
          )}`
        : "",
      previewData.effectiveAt
        ? `Fecha efectiva: ${formatDateTime(previewData.effectiveAt)}`
        : "",
      "",
      "¿Deseas aplicar este cambio?",
    ]
      .filter(Boolean)
      .join("\n")

    const confirmed = window.confirm(confirmMessage)
    if (!confirmed) return

    setSaving(true)
    setError("")
    setSuccess("")

    try {
      const result = await changeSubscriptionPlan({
        clinicId,
        planCode: selectedPlanCode,
        billingCycle: selectedBillingCycle,
      })

      setSuccess(result?.message || "Cambio de plan aplicado correctamente")
      setPreviewData(result?.preview || null)
      await loadData(clinicId)
    } catch (err) {
      setError(err.message || "No se pudo aplicar el cambio de plan")
    } finally {
      setSaving(false)
    }
  }

  const disableApply =
    !previewData ||
    !previewData.allowed ||
    previewData.action === "NO_CHANGE" ||
    previewData.mode === "BLOCKED"

  return (
    <div
      style={{
        minHeight: "100%",
        background: "#F8FAFC",
        padding: 24,
      }}
    >
      <div
        style={{
          maxWidth: 1400,
          margin: "0 auto",
          display: "grid",
          gap: 20,
        }}
      >
        <div>
          <div
            style={{
              fontSize: 30,
              fontWeight: 900,
              color: "#0F172A",
              marginBottom: 6,
            }}
          >
            Suscripción y planes
          </div>
          <div
            style={{
              fontSize: 15,
              color: "#64748B",
            }}
          >
            Aquí puedes ver el plan actual, simular el impacto del cambio y dejar
            súper claro cuándo el cambio entra de inmediato y cuándo queda
            programado para el siguiente corte.
          </div>
        </div>

        <SectionCard
          title="Contexto actual"
          subtitle="La vista debe reflejar exactamente el estado real de la suscripción"
          right={
            <button
              type="button"
              onClick={() => loadData(clinicId)}
              style={{
                border: "1px solid #CBD5E1",
                background: "#FFFFFF",
                color: "#0F172A",
                borderRadius: 12,
                padding: "10px 14px",
                fontWeight: 700,
                cursor: "pointer",
              }}
            >
              Recargar
            </button>
          }
        >
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "2fr 1fr",
              gap: 18,
            }}
          >
            <div
              style={{
                background: "#F8FAFC",
                border: "1px solid #E2E8F0",
                borderRadius: 16,
                padding: 16,
              }}
            >
              <div style={{ marginBottom: 12 }}>
                <div style={{ fontSize: 12, color: "#64748B" }}>
                  Clínica activa
                </div>
                <div
                  style={{
                    marginTop: 4,
                    fontSize: 16,
                    fontWeight: 800,
                    color: "#0F172A",
                    wordBreak: "break-all",
                  }}
                >
                  {clinicId || "No hay clínica seleccionada"}
                </div>
              </div>

              <div
                style={{
                  display: "flex",
                  flexWrap: "wrap",
                  gap: 8,
                  marginBottom: 14,
                }}
              >
                <Badge tone="neutral">
                  Fuente: {statusData?.source || "—"}
                </Badge>

                {subscription?.status ? (
                  <Badge
                    tone={
                      subscription.status === "ACTIVE"
                        ? "success"
                        : subscription.status === "TRIALING"
                        ? "info"
                        : subscription.status === "PAST_DUE"
                        ? "warning"
                        : "danger"
                    }
                  >
                    Estado: {getStatusLabel(subscription.status)}
                  </Badge>
                ) : null}

                {access?.isReadOnly ? (
                  <Badge tone="warning">Modo solo lectura</Badge>
                ) : (
                  <Badge tone="success">Acceso normal</Badge>
                )}
              </div>

              <div
                style={{
                  display: "grid",
                  gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))",
                  gap: 14,
                }}
              >
                <div
                  style={{
                    background: "#FFFFFF",
                    border: "1px solid #E5E7EB",
                    borderRadius: 14,
                    padding: 14,
                  }}
                >
                  <div style={{ fontSize: 12, color: "#64748B" }}>
                    Plan actual
                  </div>
                  <div
                    style={{
                      marginTop: 4,
                      fontSize: 22,
                      fontWeight: 900,
                      color: "#0F172A",
                    }}
                  >
                    {currentPlan?.name || "Sin plan"}
                  </div>
                  <div
                    style={{
                      marginTop: 4,
                      fontSize: 13,
                      color: "#64748B",
                    }}
                  >
                    {currentPlan?.code || "—"}
                  </div>
                </div>

                <div
                  style={{
                    background: "#FFFFFF",
                    border: "1px solid #E5E7EB",
                    borderRadius: 14,
                    padding: 14,
                  }}
                >
                  <div style={{ fontSize: 12, color: "#64748B" }}>
                    Ciclo actual
                  </div>
                  <div
                    style={{
                      marginTop: 4,
                      fontSize: 22,
                      fontWeight: 900,
                      color: "#0F172A",
                    }}
                  >
                    {getBillingCycleLabel(subscription?.billingCycle)}
                  </div>
                  <div
                    style={{
                      marginTop: 4,
                      fontSize: 13,
                      color: "#64748B",
                    }}
                  >
                    Corte actual: {formatDate(subscription?.currentPeriodEnd)}
                  </div>
                </div>

                <div
                  style={{
                    background: "#FFFFFF",
                    border: "1px solid #E5E7EB",
                    borderRadius: 14,
                    padding: 14,
                  }}
                >
                  <div style={{ fontSize: 12, color: "#64748B" }}>
                    Trial
                  </div>
                  <div
                    style={{
                      marginTop: 4,
                      fontSize: 22,
                      fontWeight: 900,
                      color: "#0F172A",
                    }}
                  >
                    {trial?.isTrial ? "Sí" : "No"}
                  </div>
                  <div
                    style={{
                      marginTop: 4,
                      fontSize: 13,
                      color: "#64748B",
                    }}
                  >
                    {trial?.isTrial
                      ? `${trial.daysRemaining || 0} días restantes`
                      : "—"}
                  </div>
                </div>
              </div>
            </div>

            <div
              style={{
                background: "#F8FAFC",
                border: "1px solid #E2E8F0",
                borderRadius: 16,
                padding: 16,
              }}
            >
              <div
                style={{
                  fontSize: 16,
                  fontWeight: 800,
                  color: "#0F172A",
                  marginBottom: 10,
                }}
              >
                Detalle operativo
              </div>

              <InfoRow label="Cuenta" value={statusData?.accountName || "—"} />
              <InfoRow
                label="Inicio del periodo"
                value={formatDateTime(subscription?.currentPeriodStart)}
              />
              <InfoRow
                label="Fin del periodo"
                value={formatDateTime(subscription?.currentPeriodEnd)}
              />
              <InfoRow
                label="Último cambio"
                value={formatDateTime(subscription?.lastPlanChangeAt)}
              />
              <InfoRow
                label="Prorrateo guardado"
                value={formatMoney(subscription?.prorationAmount)}
              />
              <InfoRow
                label="Saldo a favor"
                value={formatMoney(subscription?.creditBalance)}
              />
              <InfoRow
                label="Motivo solo lectura"
                value={access?.reason || "—"}
              />
            </div>
          </div>
        </SectionCard>

        {scheduledChange ? (
          <SectionCard
            title="Cambio ya programado"
            subtitle="Esto es lo que el usuario debe ver clarísimo para no pensar que el sistema no guardó nada"
            right={<Badge tone="warning">Pendiente de aplicar</Badge>}
          >
            <div
              style={{
                display: "grid",
                gridTemplateColumns: "repeat(auto-fit, minmax(240px, 1fr))",
                gap: 14,
              }}
            >
              <div
                style={{
                  background: "#FFF7ED",
                  border: "1px solid #FED7AA",
                  borderRadius: 16,
                  padding: 16,
                }}
              >
                <div style={{ fontSize: 12, color: "#9A3412" }}>
                  Tipo de cambio programado
                </div>
                <div
                  style={{
                    marginTop: 4,
                    fontSize: 20,
                    fontWeight: 900,
                    color: "#7C2D12",
                  }}
                >
                  {getActionLabel(scheduledChange.scheduledChangeType)}
                </div>
              </div>

              <div
                style={{
                  background: "#FFF7ED",
                  border: "1px solid #FED7AA",
                  borderRadius: 16,
                  padding: 16,
                }}
              >
                <div style={{ fontSize: 12, color: "#9A3412" }}>
                  Nuevo plan
                </div>
                <div
                  style={{
                    marginTop: 4,
                    fontSize: 20,
                    fontWeight: 900,
                    color: "#7C2D12",
                  }}
                >
                  {scheduledChange.nextPlan?.name ||
                    (scheduledChange.cancelAtPeriodEnd
                      ? "Cancelación al fin del periodo"
                      : "Sin cambio de plan")}
                </div>
                <div
                  style={{
                    marginTop: 4,
                    fontSize: 13,
                    color: "#9A3412",
                  }}
                >
                  {scheduledChange.nextPlan?.code || "—"}
                </div>
              </div>

              <div
                style={{
                  background: "#FFF7ED",
                  border: "1px solid #FED7AA",
                  borderRadius: 16,
                  padding: 16,
                }}
              >
                <div style={{ fontSize: 12, color: "#9A3412" }}>
                  Fecha efectiva
                </div>
                <div
                  style={{
                    marginTop: 4,
                    fontSize: 20,
                    fontWeight: 900,
                    color: "#7C2D12",
                  }}
                >
                  {formatDateTime(scheduledChange.scheduledChangeAt)}
                </div>
              </div>

              <div
                style={{
                  background: "#FFF7ED",
                  border: "1px solid #FED7AA",
                  borderRadius: 16,
                  padding: 16,
                }}
              >
                <div style={{ fontSize: 12, color: "#9A3412" }}>
                  Ciclo siguiente
                </div>
                <div
                  style={{
                    marginTop: 4,
                    fontSize: 20,
                    fontWeight: 900,
                    color: "#7C2D12",
                  }}
                >
                  {getBillingCycleLabel(scheduledChange.nextBillingCycle)}
                </div>
              </div>
            </div>
          </SectionCard>
        ) : null}

        {error ? (
          <div
            style={{
              background: "#FEF3F2",
              color: "#B42318",
              border: "1px solid #FECDCA",
              borderRadius: 14,
              padding: 14,
              fontWeight: 700,
            }}
          >
            {error}
          </div>
        ) : null}

        {success ? (
          <div
            style={{
              background: "#ECFDF3",
              color: "#027A48",
              border: "1px solid #A6F4C5",
              borderRadius: 14,
              padding: 14,
              fontWeight: 700,
            }}
          >
            {success}
          </div>
        ) : null}

        <SectionCard
          title="Elegir nuevo plan"
          subtitle="Aquí seleccionas el plan y el ciclo que quieres evaluar"
        >
          <div
            style={{
              display: "flex",
              gap: 12,
              flexWrap: "wrap",
              marginBottom: 18,
            }}
          >
            <label
              style={{
                display: "grid",
                gap: 6,
                minWidth: 220,
              }}
            >
              <span
                style={{
                  fontSize: 13,
                  color: "#475569",
                  fontWeight: 700,
                }}
              >
                Ciclo de facturación deseado
              </span>
              <select
                value={selectedBillingCycle}
                onChange={(e) => setSelectedBillingCycle(e.target.value)}
                style={{
                  border: "1px solid #CBD5E1",
                  background: "#FFFFFF",
                  borderRadius: 12,
                  padding: "12px 14px",
                  fontSize: 14,
                  outline: "none",
                }}
              >
                <option value="MONTHLY">Mensual</option>
                <option value="YEARLY">Anual</option>
              </select>
            </label>

            <div
              style={{
                display: "flex",
                alignItems: "flex-end",
                gap: 10,
              }}
            >
              <button
                type="button"
                onClick={handlePreview}
                disabled={loading || loadingPreview || saving || !selectedPlanCode}
                style={{
                  border: "none",
                  background: "#4F46E5",
                  color: "#FFFFFF",
                  borderRadius: 12,
                  padding: "12px 18px",
                  fontWeight: 800,
                  cursor: "pointer",
                  opacity:
                    loading || loadingPreview || saving || !selectedPlanCode
                      ? 0.6
                      : 1,
                }}
              >
                {loadingPreview ? "Generando preview..." : "Ver impacto del cambio"}
              </button>
            </div>
          </div>

          {loading ? (
            <div
              style={{
                padding: 18,
                borderRadius: 14,
                background: "#F8FAFC",
                border: "1px solid #E2E8F0",
                color: "#475569",
                fontWeight: 700,
              }}
            >
              Cargando información...
            </div>
          ) : null}

          {!loading && !clinicId ? (
            <div
              style={{
                padding: 18,
                borderRadius: 14,
                background: "#FFF7ED",
                border: "1px solid #FED7AA",
                color: "#9A3412",
                fontWeight: 700,
              }}
            >
              No hay clínica activa en localStorage (`odontoflow_clinic_id`).
            </div>
          ) : null}

          {!loading && clinicId ? (
            <div
              style={{
                display: "grid",
                gridTemplateColumns: "repeat(auto-fit, minmax(300px, 1fr))",
                gap: 16,
              }}
            >
              {plans.map((plan) => (
                <PlanCard
                  key={plan.id}
                  plan={plan}
                  isCurrent={currentPlan?.code === plan.code}
                  isSelected={selectedPlanCode === plan.code}
                  onSelect={setSelectedPlanCode}
                  selectedBillingCycle={selectedBillingCycle}
                />
              ))}
            </div>
          ) : null}
        </SectionCard>

        <SectionCard
          title="Vista previa del cambio"
          subtitle="Aquí es donde el usuario debe entender perfectamente qué va a pasar"
          right={
            previewData ? (
              <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                <Badge
                  tone={
                    previewData.mode === "BLOCKED"
                      ? "danger"
                      : previewData.mode === "SCHEDULED"
                      ? "warning"
                      : previewData.mode === "IMMEDIATE"
                      ? "success"
                      : "neutral"
                  }
                >
                  {getModeLabel(previewData.mode)}
                </Badge>

                <Badge tone="info">{getActionLabel(previewData.action)}</Badge>
              </div>
            ) : null
          }
        >
          {!previewData ? (
            <div
              style={{
                padding: 18,
                borderRadius: 14,
                background: "#F8FAFC",
                border: "1px dashed #CBD5E1",
                color: "#475569",
                fontWeight: 700,
              }}
            >
              Selecciona plan y ciclo, luego pulsa “Ver impacto del cambio”.
            </div>
          ) : (
            <div
              style={{
                display: "grid",
                gridTemplateColumns: "1.2fr 1fr",
                gap: 18,
              }}
            >
              <div
                style={{
                  background: "#F8FAFC",
                  border: "1px solid #E2E8F0",
                  borderRadius: 16,
                  padding: 16,
                }}
              >
                <div
                  style={{
                    fontSize: 16,
                    fontWeight: 800,
                    color: "#0F172A",
                    marginBottom: 12,
                  }}
                >
                  Resumen del impacto
                </div>

                <div
                  style={{
                    padding: 14,
                    borderRadius: 14,
                    background:
                      previewData.mode === "BLOCKED"
                        ? "#FEF3F2"
                        : previewData.mode === "SCHEDULED"
                        ? "#FFFBEB"
                        : "#ECFDF3",
                    border:
                      previewData.mode === "BLOCKED"
                        ? "1px solid #FECDCA"
                        : previewData.mode === "SCHEDULED"
                        ? "1px solid #FDE68A"
                        : "1px solid #A7F3D0",
                    marginBottom: 14,
                  }}
                >
                  <div
                    style={{
                      fontSize: 13,
                      color: "#475569",
                      marginBottom: 6,
                    }}
                  >
                    Explicación
                  </div>
                  <div
                    style={{
                      fontSize: 15,
                      fontWeight: 800,
                      color: "#0F172A",
                      lineHeight: 1.5,
                    }}
                  >
                    {previewData.reason || "—"}
                  </div>
                </div>

                <InfoRow
                  label="Plan actual"
                  value={`${previewData.currentPlan?.name || "—"} ${
                    previewData.currentPlan?.code
                      ? `(${previewData.currentPlan.code})`
                      : ""
                  }`}
                />
                <InfoRow
                  label="Plan destino"
                  value={`${previewData.targetPlan?.name || "—"} ${
                    previewData.targetPlan?.code
                      ? `(${previewData.targetPlan.code})`
                      : ""
                  }`}
                />
                <InfoRow
                  label="Ciclo actual"
                  value={getBillingCycleLabel(previewData.billing?.currentCycle)}
                />
                <InfoRow
                  label="Ciclo solicitado"
                  value={getBillingCycleLabel(
                    previewData.billing?.requestedCycle,
                  )}
                />
                <InfoRow
                  label="Precio actual"
                  value={formatMoney(previewData.billing?.currentPrice)}
                />
                <InfoRow
                  label="Precio objetivo"
                  value={formatMoney(previewData.billing?.targetPrice)}
                />
                <InfoRow
                  label="Prorrateo"
                  value={formatMoney(previewData.billing?.prorationAmount)}
                />
                <InfoRow
                  label="Cobro inmediato"
                  value={formatMoney(previewData.billing?.immediateCharge)}
                  valueTone={
                    Number(previewData.billing?.immediateCharge || 0) > 0
                      ? "#B45309"
                      : "#0F172A"
                  }
                />
                <InfoRow
                  label="Saldo a favor"
                  value={formatMoney(previewData.billing?.creditBalance)}
                />
                <InfoRow
                  label="Fecha efectiva"
                  value={formatDateTime(previewData.effectiveAt)}
                />
                <InfoRow
                  label="Fin del periodo actual"
                  value={formatDateTime(previewData.currentPeriodEnd)}
                />
              </div>

              <div
                style={{
                  background: "#FFFFFF",
                  border: "1px solid #E5E7EB",
                  borderRadius: 16,
                  padding: 16,
                  display: "grid",
                  alignContent: "start",
                  gap: 14,
                }}
              >
                <div>
                  <div
                    style={{
                      fontSize: 16,
                      fontWeight: 800,
                      color: "#0F172A",
                      marginBottom: 6,
                    }}
                  >
                    Qué va a pasar
                  </div>

                  <div
                    style={{
                      fontSize: 14,
                      color: "#475569",
                      lineHeight: 1.6,
                    }}
                  >
                    {previewData.action === "UPGRADE_IMMEDIATE" &&
                      "El plan sube en este momento. Se mantiene el periodo actual y solo se calcula el diferencial prorrateado del tiempo restante."}

                    {previewData.action === "DOWNGRADE_SCHEDULED" &&
                      "El plan actual se conserva hasta el final del periodo pagado. El downgrade se aplicará en la fecha de corte mostrada abajo."}

                    {previewData.action === "SCHEDULE_CYCLE_CHANGE" &&
                      "No cambia el plan hoy. Solo queda programado el cambio de ciclo para la siguiente renovación."}

                    {previewData.action === "TRIAL_CHANGE_IMMEDIATE" &&
                      "El cambio entra inmediatamente dentro del trial y no genera prorrateo."}

                    {previewData.action === "CLEAR_SCHEDULED_CHANGE" &&
                      "Se quitará el cambio pendiente y la clínica conservará el plan actual."}

                    {previewData.action === "REACTIVATION" &&
                      "Se reactiva la suscripción con un nuevo periodo vigente desde este momento."}

                    {previewData.action === "LATERAL_IMMEDIATE" &&
                      "El cambio se aplica de inmediato sin prorrateo porque no representa una subida ni bajada de precio en el ciclo actual."}

                    {previewData.action === "NO_CHANGE" &&
                      "No hace falta aplicar nada, porque el sistema ya está en ese estado."}

                    {previewData.mode === "BLOCKED" &&
                      "El cambio no puede aplicarse todavía. Primero hay que regularizar el adeudo o la condición que bloquea la suscripción."}
                  </div>
                </div>

                {previewData.pendingChange ? (
                  <div
                    style={{
                      background: "#FFF7ED",
                      border: "1px solid #FED7AA",
                      borderRadius: 14,
                      padding: 14,
                    }}
                  >
                    <div
                      style={{
                        fontSize: 13,
                        color: "#9A3412",
                        fontWeight: 800,
                        marginBottom: 6,
                      }}
                    >
                      Cambio pendiente
                    </div>

                    <div style={{ fontSize: 14, color: "#7C2D12", lineHeight: 1.6 }}>
                      {previewData.pendingChange.nextPlanId
                        ? "Quedará guardado un cambio de plan para el siguiente corte."
                        : "Quedará guardado un ajuste de ciclo para el siguiente corte."}
                    </div>
                  </div>
                ) : null}

                <button
                  type="button"
                  onClick={handleApply}
                  disabled={disableApply || saving}
                  style={{
                    border: "none",
                    background: disableApply ? "#CBD5E1" : "#111827",
                    color: "#FFFFFF",
                    borderRadius: 12,
                    padding: "14px 18px",
                    fontWeight: 900,
                    cursor: disableApply || saving ? "not-allowed" : "pointer",
                  }}
                >
                  {saving
                    ? "Aplicando..."
                    : previewData.action === "CLEAR_SCHEDULED_CHANGE"
                    ? "Cancelar cambio programado"
                    : "Confirmar y aplicar cambio"}
                </button>

                {disableApply ? (
                  <div
                    style={{
                      fontSize: 13,
                      color: "#64748B",
                      lineHeight: 1.6,
                    }}
                  >
                    {previewData.mode === "BLOCKED"
                      ? "No se puede aplicar mientras el estado esté bloqueado."
                      : previewData.action === "NO_CHANGE"
                      ? "No hay nada nuevo que aplicar."
                      : "Primero genera una vista previa válida."}
                  </div>
                ) : null}
              </div>
            </div>
          )}
        </SectionCard>

        {selectedPlan ? (
          <SectionCard
            title="Plan seleccionado"
            subtitle="Resumen rápido del plan elegido"
          >
            <div
              style={{
                display: "grid",
                gridTemplateColumns: "repeat(auto-fit, minmax(170px, 1fr))",
                gap: 10,
              }}
            >
              <div
                style={{
                  background: "#EEF2FF",
                  border: "1px solid #C7D2FE",
                  borderRadius: 14,
                  padding: 14,
                }}
              >
                <div style={{ fontSize: 12, color: "#4338CA" }}>Plan</div>
                <div
                  style={{
                    marginTop: 4,
                    fontSize: 18,
                    fontWeight: 900,
                    color: "#312E81",
                  }}
                >
                  {selectedPlan.name}
                </div>
              </div>

              <div
                style={{
                  background: "#EEF2FF",
                  border: "1px solid #C7D2FE",
                  borderRadius: 14,
                  padding: 14,
                }}
              >
                <div style={{ fontSize: 12, color: "#4338CA" }}>Código</div>
                <div
                  style={{
                    marginTop: 4,
                    fontSize: 18,
                    fontWeight: 900,
                    color: "#312E81",
                  }}
                >
                  {selectedPlan.code}
                </div>
              </div>

              <div
                style={{
                  background: "#EEF2FF",
                  border: "1px solid #C7D2FE",
                  borderRadius: 14,
                  padding: 14,
                }}
              >
                <div style={{ fontSize: 12, color: "#4338CA" }}>
                  Precio {selectedBillingCycle === "YEARLY" ? "anual" : "mensual"}
                </div>
                <div
                  style={{
                    marginTop: 4,
                    fontSize: 18,
                    fontWeight: 900,
                    color: "#312E81",
                  }}
                >
                  {selectedBillingCycle === "YEARLY"
                    ? formatMoney(selectedPlan.priceYearly ?? selectedPlan.priceMonthly * 12)
                    : formatMoney(selectedPlan.priceMonthly)}
                </div>
              </div>
            </div>
          </SectionCard>
        ) : null}
      </div>
    </div>
  )
}